import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Step, StepContent, StepLabel, Stepper, Typography } from "@mui/material";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import Step01 from "./Step01";
import Step02 from "./Step02";
import { useState } from "react";
import { IDataExpedirItem, IDataItensExpedir } from "../../../../../types/api/estoque/pesquisar-itens-para-expedir";
import { ISepararItensExpInput } from "../../../../../types/mutations/separar-expedicao-itens";
import DialogObservacaoPedidos from "./DialogObservacaoPedidos";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import useImprimirArquivo from "../../../../../lib/useImprimirArquivo";

export default function SepararDialog({
    open,
    handleClose,
    items = []
}: SepararDialogProps) {

    const queryClient = useQueryClient();
    const imprimirArquivo = useImprimirArquivo()


    const [activeStep, setActiveStep] = useState(0);
    const [observacao, setObservacao] = useState<{
        pedidos: Array<{
            codigo: string;
            observacao: string;
        }>; modal: boolean
    }>()

    const methods = useForm<Inputs>({
        defaultValues: {
            itens: items.map((a) => {
                return a.itens.map((b) => ({
                    id: b.id,
                    pedido: a.pedido,
                    produto: b.produto,
                    estoque_atual: b.estoque_atual,
                    estoque_solicitante: b.estoque_solicitante,
                    quantidade_solicitada: b.quantidade_solicitada,
                    exige_lote: b.exige_lote,
                    encomenda_solicitada: b.encomenda_solicitada,
                    quantidade: b.quantidade > b.estoque_atual ? b.estoque_atual : b.quantidade,
                    lotes: b.exige_lote && b.lotes,
                    lotes_indicados: b.exige_lote && b.lotes_indicados
                }
                ));
            }).flat(),
            pedidos: items.map((item) => {
                return {
                    codigo: item.pedido,
                    observacao: item.observacao
                }
            }),
            separador: "",
            itensSelecionados: []
        }
    });
    const { watch, handleSubmit } = methods
    const labelItensSeleiconados = watch("itensSelecionados").length <= 1 ?
        `${watch("itensSelecionados").length} item selecionado` :
        `${watch("itensSelecionados").length} itens selecionados`

    const steps = [
        { label: "Identificar separador", subLabel: watch("separador"), component: <Step01 /> },
        { label: "Selecionar itens", subLabel: labelItensSeleiconados, component: <Step02 /> }
    ]

    //queries e mutations
    const { mutate: separarExpedicaoItens, isLoading: isLoadingSeparacao } = useMutation({
        mutationKey: "separar-expedicao-itens",
        mutationFn: (inputs: ISepararItensExpInput) => axiosClient.post("/estoque/separar-expedicao-itens", inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao enviar itens para separação', { variant: 'error' });
        },
        onSuccess: ({ data }) => {
            enqueueSnackbar('Itens enviados para separação', { variant: 'success' });
            queryClient.invalidateQueries("pesquisar-expedicoes");
            queryClient.invalidateQueries("pesquisar-itens-para-expedir");
            imprimirArquivo(`estoque/imprimir-expedicao/${data}`)
            handleClose()
        }
    });
    //


    //funções manipuladoras
    const onSubmit: SubmitHandler<Inputs> = (inputs) => {
        const arrItens = inputs.itensSelecionados.map((item) => ({
            id: item.id,
            quantidade: item.quantidade,
            ...(item.lotes ? { lotes: item.lotes_indicados } : {})
        }))
        const params = {
            nome_separador: inputs.separador,
            itens: arrItens
        }
        separarExpedicaoItens(params);
    }

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };
    //

    //valores importantes
    const itensNaoSelecionados = activeStep === 1 && watch("itensSelecionados").length === 0
    const observacoesIsEmpty = watch('pedidos').map((item) => item.observacao).some((i) => i !== '')
    //

    return (
        <Dialog
            fullWidth
            open={open}
            onClose={handleClose}
            maxWidth={activeStep === 0 ? "sm" : "lg"}
        >
            <Box display={'flex'} justifyContent={'space-between'}>
                <DialogTitle>Enviar itens para separação</DialogTitle>
                <Box paddingX={3} paddingY={2}>
                    {
                        observacoesIsEmpty &&
                        <Button
                            startIcon={observacao?.modal ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            onClick={() => setObservacao({
                                pedidos: watch('pedidos')
                                    .filter((item) => item.observacao !== '')
                                    .map((item) => ({
                                        codigo: item.codigo,
                                        observacao: item.observacao
                                    })),
                                modal: true
                            })}
                        >
                            Observações
                        </Button>
                    }
                    {
                        observacao?.modal &&
                        <DialogObservacaoPedidos
                            open={observacao.modal}
                            pedidos={observacao.pedidos}
                            handleClose={() => setObservacao({ pedidos: [], modal: false })}
                        />
                    }
                </Box>
            </Box>

            <DialogContent>
                <FormProvider {...methods}>
                    <Box width="100%">
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {steps.map((step, index) => {

                                let labelBtn: string = ""
                                let actionBtn: TActionBtn = () => { }
                                switch (activeStep) {
                                    case 0:
                                        labelBtn = "Avançar"
                                        actionBtn = handleNext
                                        break;

                                    case 1:
                                        labelBtn = "Separar"
                                        actionBtn = onSubmit
                                        break;
                                }

                                return (
                                    <Step key={step.label}>
                                        <StepLabel
                                            optional={activeStep > index && <Typography variant="caption">{step.subLabel}</Typography>}
                                        >
                                            {step.label}
                                        </StepLabel>

                                        <StepContent>
                                            {step.component}

                                            <Box sx={{ mb: 2 }}>
                                                <>
                                                    <Button
                                                        disabled={itensNaoSelecionados || isLoadingSeparacao}
                                                        variant="contained"
                                                        onClick={handleSubmit(actionBtn)}
                                                        sx={{ mt: 1, mr: 1 }}
                                                    >
                                                        {
                                                            isLoadingSeparacao &&
                                                            <CircularProgress size={20} sx={{ mr: 1.5 }} />
                                                        }
                                                        {labelBtn}
                                                    </Button>

                                                    {
                                                        index !== 0 &&
                                                        <Button
                                                            onClick={handleBack}
                                                            sx={{ mt: 1, mr: 1 }}
                                                        >
                                                            Voltar
                                                        </Button>
                                                    }
                                                </>
                                            </Box>
                                        </StepContent>
                                    </Step>
                                )
                            })}
                        </Stepper>
                    </Box>
                </FormProvider>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleClose}
                >
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface SepararDialogProps {
    open: boolean,
    handleClose: () => void;
    items: IDataItensExpedir[]
}


export type Inputs = {
    separador: string;
    pedidos: Array<{
        codigo: string;
        observacao: string;
    }>
    itens: Array<{
        id: number;
        pedido: string;
        produto: {
            codigo: number;
            nome: string;
            departamento: string;
            setor: string;
            unidade_medida: string;
        };
        estoque_atual: number;
        quantidade_solicitada: number;
        exige_lote: boolean;
        quantidade: number;
        lotes: Array<{ estoque_atual: number; fabricacao: string; lote: string; vencimento: string }> | boolean;
        lotes_indicados: Array<{ lote: string; quantidade: number; }> | boolean;
    }>;
    itensSelecionados: IDataExpedirItem[]
}

type TActionBtn = (arg: Inputs) => void